/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import debounce from 'lodash.debounce'
import {
  Input,
  ListUi,
  Spinner,
} from '@vwfs-bronson/bronson-react'
import { useField } from 'formik'
import './AddressAutocomplete.css'
import { useTranslation } from 'react-i18next'
import { autocomplete } from '../../services/api/addressValidation/autocomplete'
import { FormField } from '../FormField'
import { getListItemChildren } from './common'

interface Props {
  prefixCode?: string
  addressName?: string
  label?: string
}

const DEBOUNCE_MILLIS = 300
const MIN_QUERY_LENGTH = 2

export const StreetAutocomplete = ({ prefixCode, addressName = 'address', label }: Props) => {
  const { t } = useTranslation()
  const [fieldProps, , { setValue: setAutocompleteQuery }] = useField<string>(
    `customerData.${addressName}.street`
  )
  const [{ value: countryCode }] = useField<string>(
    `customerData.${addressName}.countryCode`
  )
  const [{ value: zipCode }] = useField<string>(`customerData.${addressName}.zipCode`)
  const [{ value: city }] = useField<string>(`customerData.${addressName}.city`)
  const [{ value: zipCodeSelected }] = useField<boolean>(`zipCodeSelected`)
  const [, , { setValue: setStreetSelected }] =
    useField<boolean>(`streetSelected`)

  const abortController = useRef<AbortController>()
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string>()
  const [items, setItems] = useState<any[]>([])
  const [currentItem, setCurrentItem] = useState<any>()
  const [initialized, setInitialized] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (
      currentItem &&
      (fieldProps?.value !== currentItem.street ||
        zipCode !== currentItem.zipCode ||
        city !== (currentItem.city || currentItem.county) ||
        countryCode !== currentItem.countryCode)
    ) {
      setError(undefined)
      setItems([])
      setCurrentItem(undefined)
      setStreetSelected(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldProps?.value, zipCode, city, countryCode])

  useEffect(() => {
    if (initialized) {
      if (currentItem && fieldProps?.value !== currentItem.street) {
        setCurrentItem(undefined)
      }
      onDebouncedAutocompleteQueryChange((fieldProps?.value ?? '').trim())
    } else {
      setInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldProps?.value])

  const onAutocompleteQueryChange = (newQuery: string) => {
    if (newQuery === query && !error) {
      return
    }
    // eslint-disable-next-line no-unused-expressions
    abortController.current?.abort()
    setQuery(newQuery)
    setError(undefined)
    setItems([])
    if (newQuery.length < MIN_QUERY_LENGTH) {
      abortController.current = undefined
      return
    }
    abortController.current = new AbortController()
    setLoading(true)
    autocomplete(
      {
        street: fieldProps?.value,
        countryCode: prefixCode,
        ...(city && { city }),
        ...(zipCode && { zipCode }),
      },
      abortController.current
    )
      .then((i) => {
        setError(
          i.length === 0
            ? t('personal-details:errors:noAddressFound')
            : undefined
        )
        setItems(i)
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setError(t('personal-details:errors:genericError'))
          setItems([])
        }
      })
      .finally(() => {
        abortController.current = undefined
        setLoading(false)
      })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onDebouncedAutocompleteQueryChange = debounce(
    onAutocompleteQueryChange,
    DEBOUNCE_MILLIS
  )

  return (
    <div className="address-autocomplete">
      <FormField
        testId="street"
        type="input"
        labelText={label}
        required
        {...fieldProps}
        render={(props) => {
          return (
            <Input {...props} disabled={!zipCodeSelected} autoComplete="off" />
          )
        }}
      />
      {items && items.length > 0 && (
        <div className="address-autocomplete">
          <ListUi>
            {items.map((item, index) => (
              <ListUi.Item
                key={index}
                onClick={() => {
                  setStreetSelected(true)
                  setQuery(item.street)
                  setAutocompleteQuery(item.street, false)
                  setCurrentItem(item)
                  setError(undefined)
                  setItems([])
                }}
              >
                {getListItemChildren(item)}
              </ListUi.Item>
            ))}
          </ListUi>
        </div>
      )}
      {!items.length && loading && (
        <ListUi>
          <ListUi.Item key="streetLoading">
            <Spinner small />
          </ListUi.Item>
        </ListUi>
      )}
    </div>
  )
}
